// Visualization3.js
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './css/Visualization3.css';

function Visualization3() {
  const svgRef = useRef();
  const [legendData, setLegendData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const csvData = await d3.csv(`${process.env.PUBLIC_URL}/data/co2_emissions.csv`, row => {
        const parsedRow = { Characteristic: row.Characteristic };
        Object.keys(row).forEach(key => {
          if (key !== 'Characteristic') {
            parsedRow[key] = parseFloat(row[key].replace(/,/g, ''));
          }
        });
        return parsedRow;
      });
      createLineChart(csvData);
    };

    fetchData();
  }, []);

  const createLineChart = (data) => {
    const margin = { top: 20, right: 30, bottom: 50, left: 80 };
    const width = 800 - margin.left - margin.right;
    const height = 600 - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const parseYear = d3.timeParse('%Y');

    data.sort((a, b) => d3.ascending(parseYear(a.Characteristic), parseYear(b.Characteristic)));

    const years = data.map(d => parseYear(d.Characteristic));
    const regions = Object.keys(data[0]).filter(key => key !== 'Characteristic');

    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

    const x = d3.scalePoint()
      .domain(years)
      .range([0, width]);

    const yMax = d3.max(data, d => Math.max(...regions.map(region => d[region])));
    const y = d3.scaleLinear()
      .domain([0, Math.ceil(yMax / 2000) * 1850])
      .range([height, 0]);

    const line = d3.line()
      .defined(d => !isNaN(d.value))
      .x(d => x(parseYear(d.Characteristic)))
      .y(d => y(d.value));

    regions.forEach(region => {
      const regionData = data.map(d => ({
        Characteristic: d.Characteristic,
        value: d[region]
      })).filter(d => !isNaN(d.value));

      // Draw the line
      svg.append('path')
        .datum(regionData)
        .attr('fill', 'none')
        .attr('stroke', colorScale(region))
        .attr('stroke-width', 1.5)
        .attr('d', line);

      // Draw the data points
      svg.selectAll(`.datapoint-${region}`)
        .data(regionData)
        .enter().append("circle")
        .attr("class", `datapoint-${region}`)
        .attr("cx", d => x(parseYear(d.Characteristic)))
        .attr("cy", d => y(d.value))
        .attr("r", 4)
        .attr("fill", colorScale(region))
        .on("mouseover", function(event, d) {
          // Enlarge and add border on hover
          d3.select(this)
            .transition()
            .duration(200)
            .attr("r", 6) // Enlarge radius
            .style("stroke", "black") // Add black border
            .style("stroke-width", "2"); // Border width
          showTooltip(event, d);
        })
        .on("mouseout", function() {
          // Reset size and remove border on mouseout
          d3.select(this)
            .transition()
            .duration(200)
            .attr("r", 4) // Reset radius
            .style("stroke", "none"); // Remove border
          hideTooltip();
        });
    });

    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).tickFormat(d3.timeFormat('%Y')))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    svg.append('g')
      .call(d3.axisLeft(y).ticks(yMax / 2000));

    // Add x-axis label
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', height + margin.bottom - 10)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Year');

    // Add y-axis label
    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', -margin.left + 20)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .html('Emissions in MtCO<tspan style="font-size: 8px; vertical-align: sub;">2</tspan>'); // Use subscript for the '2'

    setLegendData(regions.map(region => ({ region, color: colorScale(region) })));

    // Tooltip setup
    const tooltip = d3.select("body").append("div")
      .attr("class", "tooltip")
      .style("opacity", 0);

    function showTooltip(event, d) {
      const year = d.Characteristic;
      const allRegionsData = data.find(item => item.Characteristic === year);

      let tooltipContent = `<strong>Year: ${year}</strong><br>`;
      regions.forEach(region => {
        tooltipContent += `${region}: ${allRegionsData[region]}<br>`;
      });

      tooltip.transition()
        .duration(200)
        .style("opacity", 0.9);

      tooltip.html(tooltipContent)
        .style("left", (event.pageX + 10) + "px")
        .style("top", (event.pageY - 28) + "px");
    }

    function hideTooltip() {
      tooltip.transition()
        .duration(500)
        .style("opacity", 0);
    }
  };

  return (
    <>
      <div className="header-container">
        <h2>
          Carbon dioxide emissions from energy worldwide from 1965 to 2023, by region
        </h2>
        <p>
          Global CO₂ emissions from energy consumption have more than doubled since 1970, and reached a record high in 2023. The rise in emissions is mainly due to rapidly growing economies and increasing energy demand in developing regions.
        </p>
      </div>
      
       <div className="visualization-container">
         <svg ref={svgRef}></svg>
         <div className="legend">
           {legendData.map((item, index) => (
             <div key={index} className="legend-item">
               <span
                 className="legend-color"
                 style={{ backgroundColor: item.color }}
               ></span>
               <span className="legend-label">{item.region}</span>
             </div>
           ))}
         </div>
       </div>
     </>
   );
}

export default Visualization3;